import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Badge
} from '@mui/material';
import {
  Security as SecurityIcon,
  Api as ApiIcon,
  Shield as ShieldIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import {getAllEndpoints, getEndpointsWithRoles,
    getEndpointsWithPreAuthorize, getSecurityMappings
} from '../../services/devAPIs';

const EndpointsInspector = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    allEndpoints: [],
    endpointsWithRoles: [],
    preAuthorizeEndpoints: [],
    securityMappings: []
  });

  const loadData = async (tabIndex) => {
    setLoading(true);
    try {
      const apiCalls = [
        getAllEndpoints,
        getEndpointsWithRoles,
        getEndpointsWithPreAuthorize,
        getSecurityMappings
      ];
      
      const keys = ['allEndpoints', 'endpointsWithRoles', 'preAuthorizeEndpoints', 'securityMappings'];
      const result = await apiCalls[tabIndex]();
      setData(prev => ({ ...prev, [keys[tabIndex]]: result }));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(activeTab);
  }, [activeTab]);

  const getMethodColor = (method) => {
    const m = method.toUpperCase();
    if (m.includes('GET')) return { bg: '#28a745', text: '#fff' };
    if (m.includes('POST')) return { bg: '#007bff', text: '#fff' };
    if (m.includes('PUT')) return { bg: '#fd7e14', text: '#fff' };
    if (m.includes('DELETE')) return { bg: '#dc3545', text: '#fff' };
    return { bg: '#6c757d', text: '#fff' };
  };

  const getRoleColor = (role) => {
    const r = role.toLowerCase().split('_')[0];
    const colors = {
      dev: '#e83e8c',
      hr: '#20c997',
      manager: '#fd7e14',
      director: '#6f42c1',
      employee: '#6c757d'
    };
    return colors[r] || '#17a2b8';
  };

  const MethodBadge = ({ method }) => {
    const colors = getMethodColor(method);
    return (
      <Chip
        label={method.replace(/[\[\]]/g, '')}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          fontWeight: 600,
          minWidth: 70,
          fontSize: '0.75rem'
        }}
      />
    );
  };

  const RolePill = ({ role }) => (
    <Chip
      label={role}
      size="small"
      sx={{
        backgroundColor: getRoleColor(role),
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}
    />
  );

  const tabData = [
    { label: 'All Endpoints', icon: <ApiIcon />, count: data.allEndpoints.length },
    { label: 'With Roles', icon: <SecurityIcon />, count: data.endpointsWithRoles.length },
    { label: '@PreAuthorize', icon: <ShieldIcon />, count: data.preAuthorizeEndpoints.length },
    { label: 'Security Mappings', icon: <LockIcon />, count: data.securityMappings.length }
  ];

  return (
    <Box sx={{ minHeight: '100vh', py: 2 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 4,
            borderRadius: 3,
            mb: 4,
            textAlign: 'center'
          }}
        >
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            🔍 API Endpoints Inspector
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Monitor and debug your Spring Boot API endpoints and security configurations
          </Typography>
        </Paper>

        {/* Tabs */}
        <Paper elevation={0} sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600
              },
              '& .Mui-selected': {
                color: '#667eea'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea',
                height: 3
              }
            }}
          >
            {tabData.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2}}>
                    {tab.label}
                    <Badge
                      badgeContent={tab.count}
                      max={9999} 
                      color="primary"
                      sx={{
                        ml:2,
                        '& .MuiBadge-badge': {
                          backgroundColor: activeTab === index ? '#667eea' : '#e0e0e0',
                          color: activeTab === index ? '#fff' : '#666'
                        }
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={50} sx={{ mb: 2, color: '#667eea' }} />
            <Typography variant="h6" color="text.secondary">
              Loading endpoint data...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* All Endpoints Tab */}
            {activeTab === 0 && data.allEndpoints.map((endpoint, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <MethodBadge method={endpoint.httpMethod} />
                      <Typography
                        component="code"
                        sx={{
                          backgroundColor: '#f8f9fa',
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontFamily: 'monospace',
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {endpoint.path}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Controller:
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                          {endpoint.controller}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Method:
                        </Typography>
                        <Typography component="code" variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {endpoint.method}()
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Endpoints with Roles Tab */}
            {activeTab === 1 && data.endpointsWithRoles.map((endpoint, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    borderLeft: '4px solid #28a745',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <MethodBadge method={endpoint.httpMethod} />
                      <Typography
                        component="code"
                        sx={{
                          backgroundColor: '#f8f9fa',
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontFamily: 'monospace',
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {endpoint.path}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Controller:
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                          {endpoint.controller}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                          Required Roles:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {endpoint.roles && endpoint.roles !== 'N/A' ? (
                            endpoint.roles.split(', ').map((role, idx) => (
                              <RolePill key={idx} role={role.trim()} />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No specific roles required
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* PreAuthorize Tab */}
            {activeTab === 2 && data.preAuthorizeEndpoints.map((endpoint, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    borderLeft: '4px solid #dc3545',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <MethodBadge method={endpoint.method} />
                      <Typography
                        component="code"
                        sx={{
                          backgroundColor: '#f8f9fa',
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontFamily: 'monospace',
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {endpoint.path}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Controller:
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                          {endpoint.controller}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                          Security Expression:
                        </Typography>
                        <Typography
                          component="code"
                          sx={{
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            fontSize: '0.8rem',
                            fontFamily: 'monospace',
                            color: '#856404',
                            display: 'block',
                            wordBreak: 'break-all'
                          }}
                        >
                          {endpoint.roles}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Security Mappings Tab */}
            {activeTab === 3 && data.securityMappings.map((mapping, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent>
                    {mapping.info ? (
                      <Box sx={{ backgroundColor: '#d1ecf1', color: '#0c5460', p: 2, borderRadius: 1, border: '1px solid #bee5eb' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {mapping.info}
                        </Typography>
                      </Box>
                    ) : mapping.error ? (
                      <Box sx={{ backgroundColor: '#f8d7da', color: '#721c24', p: 2, borderRadius: 1, border: '1px solid #f5c6cb' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {mapping.error}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                          <MethodBadge method={mapping.method} />
                          <Typography
                            component="code"
                            sx={{
                              backgroundColor: '#f8f9fa',
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 1,
                              fontSize: '0.875rem',
                              fontFamily: 'monospace',
                              flexGrow: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {mapping.path}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            Allowed Roles:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {mapping.roles && mapping.roles.map((role, idx) => (
                              <RolePill key={idx} role={role} />
                            ))}
                          </Box>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default EndpointsInspector;